import {BaseAreaView, IView, IViewApi, IViewContainer, OBaseAreaViewInit, OViewVisitOptions, VIEWS} from "/@lib@/commons/views.js";
import {IReg, IRegPointer, REG} from "/@lib@/commons/registry.js";
import {IDepotResUiEnv, IDepotShortResUiEnv, IDepotUiEnv, JStoreChange} from "/@lib@/store/depot.js";
import {InfoCurrentRes, InfoFocusRes, InfoReqCurrentRes, InfoUpdatePendingRes, IResType, resPath} from "/@lib@/store/res.js";
import {EHttpStatusCode, isRespError} from "/@lib@/commons/io/io.js";
import {JSX} from "/@lib@/commons/xml/dom.js";
import {BaseElement, BaseElementAsync, BASIS, isEltRefreshable, MsgOver, OSkinableInit} from "/@back@/commons/basis.js";
import {JLastDatas, OLastDatasInit} from "/@lib@/commons/lastDatas.js";
import {IInfo, IInfoConsumer} from "/@lib@/commons/infos.js";
import {URLTREE} from "/@lib@/store/urlTree.js";
import {DOMSH} from "/@lib@/commons/xml/domsh.js";
import {BarActions, OBarActionsInit} from "/@back@/commons/widgets/bars.js";

/**
 * Interface pour tout composant susceptible d'afficher une ressource
 * dans un contexte donné.
 */
export interface IResViewable {
	setResPath(resPath: resPath): any
}


/**
 * Viewer d'une seule res courante (ie pas d'onglets de N res : todo ResViewerMulti...).
 * Répond aux requetes InfoReqCurrentRes et dispatch les InfoCurrentRes en cas de changement de path.
 */
export interface ResViewerSingle extends BaseElement {
	initialize(init: OResViewerSingleInit): this
}

export interface OResViewerSingleInit extends OSkinableInit, OLastDatasInit {

	reg?: IReg<IDepotUiEnv>

	/** Key de la view à afficher issue de IResType.resView(key) */
	keyView?: string

	/** Force ce path à se charger (surcharge les lastDatas : path imposé par l'URL...) */
	startWithResPath?: resPath
}

export class ResViewerSingle extends BaseElement implements IResViewable, IViewContainer, IInfoConsumer {

	reg: IReg<IDepotUiEnv>;

	/** */
	view: ResRoot;

	keyView: string;

	protected _lastDatas: any;

	protected _shown: boolean;

	async setResPath(resPath: resPath, lastDatas?: JLastDatas): Promise<void> {
		if (this.view) {
			if (this.view.resPath === resPath) return; //Pas de chgt
			VIEWS.onViewHidden(this.view, true);
			this.view.remove();
			this.view = null;
		} else if (resPath == null) return; //Pas de chgt : pas de res courante.

		if (resPath != null) {
			//Chargement du resReg
			const resReg = this.reg.env.universe.newDepotResUiRegFromDepotUiReg(this.reg, resPath);
			while (resReg.env.nodeInfosPending) await resReg.env.nodeInfosPending;
			await this.reg.env.universe.getRenderings(resReg);
			if (this.view != null) return; //raceCond
			if (!resReg.env.nodeInfos) {
				return this.setResPath(URLTREE.extractParentPath(resPath)); //pas trouvé, on remonte au père.
			} else {
				//Ajustement du path du reg au livePath.
				resReg.env.path = resReg.env.resType.livePath(resReg.env.nodeInfos.permaPath);
				const view = this.view = new ResRoot().initialize({
					reg: this.reg,
					keyView: this.keyView,
					resReg,
					lastDatas,
					onErrorFallback: this.onErrorFallback
				});
				this.shadowRoot.appendChild(view);
				if (this._shown) VIEWS.onViewShown(view);
				this.reg.env.infoBroker?.dispatchInfo(new InfoCurrentRes(this.view.resPath, this.view.reg.env.nodeInfos), this);
			}
		} else {
			this.reg.env.infoBroker?.dispatchInfo(new InfoCurrentRes(null), this);
		}
	}

	protected _initialize(init: OResViewerSingleInit) {
		this.reg = this.findReg(init);
		this.keyView = init.keyView;
		this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this._initAndInstallSkin(this.localName, init);
		this.reg.env.infoBroker?.addConsumer(this);
		this.reg.env.depotEvents?.on("resChange", (this._onResChange = this.onResChange.bind(this)));
		this.reg.env.depotEvents?.on("connOpened", (this._onConnOpened = this.onConnOpened.bind(this)));
		let startPath = init.startWithResPath;
		if (startPath == null && init.lastDatas) startPath = (init.lastDatas as JLDResViewerSingle).lastPath;
		if (startPath != null) this.setResPath(startPath, init.lastDatas);
	}

	onResChange(m: JStoreChange) {
		if (this.view) {
			//On évalue si un chgt du resPath de la vue est nécessaire
			//Si m n'implique un cght de resPath ce sont les listeners dan view qui feront les refresh nécessaires.
			const livePath = this.view.reg.env.resType?.livePath(m.props.path);
			if ((livePath !== m.props.path || m.props.movedTo) && livePath === this.view.resPath) {
				this.setResPath(m.props.movedTo || m.props.path);
			}
		}
	}

	protected _onResChange: (m: JStoreChange) => void;

	onConnOpened() {
		if (this.view && !this.view.reg.env.nodeInfosPending) {
			//Vue déjà chargée
			const path = this.view.resPath;
			this.setResPath(null);
			this.setResPath(path);
		}
	}

	protected _onConnOpened: () => void;

	visitViews(visitor: (view: IViewApi) => any, options?: OViewVisitOptions): any {
		if (!this.view) return;
		if (options?.visible && !this._shown) return;
		return visitor(this.view);
	}

	visitViewsAsync(visitor: (view: IViewApi) => Promise<any>, options?: OViewVisitOptions): Promise<any> {
		if (!this.view) return;
		if (options?.visible && !this._shown) return;
		return visitor(this.view);
	}

	onViewShown() {
		this._shown = true;
		if (this.view) VIEWS.onViewShown(this.view);
	}

	onViewHidden(closed?: boolean) {
		this._shown = false;
		try {
			if (this.view) VIEWS.onViewHidden(this.view, closed);
		} finally {
			if (closed) {
				this.view = null;
				this.reg.env.infoBroker?.removeConsumer(this);
				this.reg.env.depotEvents?.removeListener("resChange", this._onResChange);
				this.reg.env.depotEvents?.removeListener("connOpened", this._onConnOpened);
			}
		}
	}

	protected _refresh() {
		this.view?.refresh();
	}

	onInfo(info: IInfo): void {
		if (info instanceof InfoReqCurrentRes) {
			if (this.view) {
				info.resPath = this.view.resPath;
				info.resProps = this.view.reg.env.nodeInfos;
			}
		} else if (info instanceof InfoFocusRes) {
			this.setResPath(info.resPath);
			info.focusHandled = this;
		} else if (info instanceof InfoUpdatePendingRes) {
			const current = this.view?.resPath;
			if (current) {
				const currentType = this.view.reg.env.resType;
				if (currentType && currentType.livePath(info.resPath) === current) {
					//this.setResPath(null);
					const msg = new MsgOver().setCustomMsg("Enregistrement en cours...", "info").showMsgOver(this.view);
					info.done.then((r) => {
						if (this.view?.resPath === current) {
							if (r === null) {
								//noeud supprimé, on remonte au père.
								this.setResPath(URLTREE.extractParentPath(current));
							} else {
								this.setResPath(r.path);
							}
						}
					}).catch((e) => {
						//Rechargement de l'ancien path
						if (this.view?.resPath === current) this.setResPath(current);
					}).finally(() => {msg.removeMsg()});
				}
			}
		}
	}

	onErrorFallback(this: void, resRoot: ResRoot) {
		const me = DOMSH.findHost<ResViewerSingle>(resRoot);
		me.setResPath(URLTREE.extractParentPath(resRoot.resPath));
	}
}

export interface JLDResViewerSingle extends JLastDatas {
	lastPath?: string
}

REG.reg.registerSkin('store-resviewer-single', 1, /* language=CSS */ `
	:host {
		flex: 1;
		display: flex;
		min-width: 0;
		min-height: 0;
		flex-direction: column;
	}

	store-res-root {
		flex: 1;
		display: flex;
		min-height: 0;
		min-width: 0;
	}
`);

customElements.define('store-resviewer-single', ResViewerSingle);


/**
 * Racine d'une vue d'une Res pour un resPath **fixé** qui instancie
 * dynamiquement son propre registre IDepotResUiEnv enrichi par son IResType.
 *
 * Si une modif de la res entraine un changement de type, le registre est reconstruit.
 *
 * TODO impl IView avec les attr icon, label, description et VIEWS.dispatchViewChange(this)
 * 	si on veut qu'un container de type ResViewerMulti avec onglets par exemple soit mis à jour...
 */
export interface ResRoot extends BaseElement, IViewContainer {
	initialize(init: OResRootInit): this
}

export interface OResRootInit extends OLastDatasInit {

	reg?: IReg<IDepotUiEnv>

	/** Key de la view à afficher issue de IResType.resView(key) */
	keyView?: string

	/** Path fixé (généralement le resLivePath). Utilisé si resReg non renseigné. */
	resPath?: resPath

	/** Registre déjà instancié. Si spécifié, resPath non utilisé. */
	resReg?: IReg<IDepotResUiEnv>

	/** En cas d'erreur (contenu supprimé, accès refusé, echec...)*/
	onErrorFallback?: (resRoot: ResRoot) => void
}

export class ResRoot extends BaseElementAsync implements IViewContainer, IView {

	keyView: string;

	dptReg: IReg<IDepotUiEnv>;

	resPath: resPath;

	onErrorFallback?: (resRoot: ResRoot) => void

	/** Registre instancié propre à la Res. */
	reg: IReg<IDepotResUiEnv>;

	/** resView dépendante de son type. */
	view: IView;

	/** Mémoire du resType associé. */
	resType: IResType;

	/** Mémoire transitoire des lastDatas pour le 1er chargement. */
	protected _lastDatas: any;

	protected _shown: boolean;

	visitViews(visitor: (view: IViewApi) => any, options?: OViewVisitOptions): any {
		if (!this.view) return;
		if (options?.visible && !this._shown) return;
		return visitor(this.view);
	}

	visitViewsAsync(visitor: (view: IViewApi) => Promise<any>, options?: OViewVisitOptions): Promise<any> {
		if (!this.view) return;
		if (options?.visible && !this._shown) return;
		return visitor(this.view);
	}

	onViewShown() {
		this._shown = true;
		if (this.view) VIEWS.onViewShown(this.view);
	}

	onViewHidden(closed?: boolean) {
		this._shown = false;
		try {
			if (this.view) VIEWS.onViewHidden(this.view, closed);
		} finally {
			if (closed) this.reg.close();
		}
	}

	protected async _initialize(init: OResRootInit) {
		this.dptReg = init.reg;
		this.keyView = init.keyView || "main";
		this.onErrorFallback = init.onErrorFallback;
		if (init.lastDatas) this._lastDatas = init.lastDatas;
		if (init.resReg) {
			this.resPath = init.resReg.env.path;
			this.setResReg(init.resReg);
		} else {
			this.resPath = init.resPath;
			this.createResReg();
		}
	}

	protected createResReg() {
		this.setResReg(this.dptReg.env.universe.newDepotResUiRegFromDepotUiReg(this.dptReg, this.resPath));
	}

	protected setResReg(reg: IReg<IDepotResUiEnv>) {
		this.reg = reg;
		const lstn = this.reg.env.nodeInfosChange;
		if (lstn) lstn.on("nodeChange", this.refresh.bind(this));
		else this.reg.env.nodeInfosPending?.then(this.refresh.bind(this));
	}

	protected _refresh() {
		const env = this.reg.env;
		if (env.nodeInfos) {
			//nodeInfos chargé
			if (env.resType !== this.resType) {
				//1er refresh ou chgt de type de res
				let lastDatas;
				if (this.resType) {
					//C'est un chgt de type de res.
					this.reg.close();
					this.createResReg();
				} else if (this._lastDatas) {
					lastDatas = this._lastDatas;
					this._lastDatas = null;
				}
				this.resType = env.resType;
				this.draw(lastDatas);
			} else {
				//Reconstruction de la view
				this.draw(null);
			}
		} else if (env.nodeInfosPending) {
			//nodeInfos en chargement
		} else {
			//Pas de nodeInfos, ni de chargement en cours => erreur
			this.resType = null;
			this.drawError();
		}
	}

	protected async draw(lastDatas: null | JLastDatas) {
		this.view = await this.resType.resView(this.keyView).loadBody(this.reg, lastDatas);
		if (this.reg.isClosed) return; //raceCond
		this.clear();
		this.appendChild(this.view);
		if (this._shown) VIEWS.onViewShown(this.view);
		//TODO avec impl IView VIEWS.dispatchViewChange(this);
	}

	protected clear() {
		VIEWS.clearContent(this, true);
	}

	protected drawError() {
		this.clear();
		if (this.reg.env.nodeInfos === null) {
			this.appendChild(<c-msg level="warning" label="Ce contenu n'existe pas ou a été supprimé"/>);
		} else {
			const err = this.reg.env.nodeInfosError;
			if (isRespError(err) && err.response.status == EHttpStatusCode.forbidden) {
				this.appendChild(<c-msg level="warning" label="Vous ne disposez pas des permissions pour consulter ce contenu"/>);
			} else {
				this.appendChild(<c-msg level="error" label="Erreur au chargement"/>);
			}
		}
		if (this.onErrorFallback) this.onErrorFallback(this);
	}
}

customElements.define('store-res-root', ResRoot);


export interface OResBodyViewInit extends OBaseAreaViewInit<IRegPointer<IDepotResUiEnv | IDepotShortResUiEnv>> {
	actionsBar?: OBarActionsInit<IRegPointer<IDepotResUiEnv | IDepotShortResUiEnv>>
}

/**
 * Base aux views des res affichant un contenu avec gestion d'erreur.
 */
export abstract class ResBodyView extends BaseAreaView<IRegPointer<IDepotResUiEnv | IDepotShortResUiEnv>> {

	reg: IReg<IDepotResUiEnv | IDepotShortResUiEnv>;

	protected _refresh() {
		const env = this.reg.env;
		if (env.nodeInfos) {
			this.clear();
			this.draw();
		} else if ((env as IDepotResUiEnv).nodeInfosPending) {
			(env as IDepotResUiEnv).nodeInfosPending.then(this.refresh.bind(this));
		} else {
			//Pas de nodeInfos, ni de chargement en cours => erreur
			this.clear();
			this.drawError();
		}
	}

	protected initBarActions(init: OResBodyViewInit, parent: Node) {
		if (init.actionsBar) parent.appendChild(<BarActions id="bodyActions" î={BASIS.newInit(init.actionsBar, this.reg)}/>);
	}

	protected abstract draw(): void;

	protected clear() {
		BASIS.clearContent(this.shadowRoot);
	}

	protected drawError() {
		if (this.reg.env.nodeInfos === null) {
			this.shadowRoot.appendChild(<c-msg level="warning" label="Ce contenu n'existe pas ou a été supprimé"/>);
		} else {
			const err = (this.reg.env as IDepotResUiEnv).nodeInfosError;
			if (isRespError(err) && err.response.status == EHttpStatusCode.forbidden) {
				this.shadowRoot.appendChild(<c-msg level="warning" label="Vous ne disposez pas des permissions pour consulter ce contenu"/>);
			} else {
				this.shadowRoot.appendChild(<c-msg level="error" label="Erreur au chargement"/>);
			}
		}
	}
}